import { Airline4XRules } from '../constants/server-constants';
/**
 * Airports Domain
 * Types, store, handlers, and service methods for airport management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cities } from '../data/cities';

export const AIRPORT_THRESHOLDS = Airline4XRules.airport_levels.map(level => level.influence);
/** Each airport development level's name, by the level: 0, Undeveloped, to 6, a World Hub. Keys written whole. */
const AIRPORT_LEVEL_WORDS = [
  'fourX.level.Undeveloped',
  'fourX.level.Airfield',
  'fourX.level.Regional',
  'fourX.level.Major',
  'fourX.level.International',
  'fourX.level.Global',
  'fourX.level.WorldHub',
] as const;

/**
 * How far into a city a team has come, by its Influence there: no presence
 * yet, then the rules' four depths (city_depths in airline-4x-rules.json).
 */
type Depth = 'none' | 'presence' | 'base' | 'franchise' | 'stronghold';

/**
 * The rules' four depths, shallowest first, each with the Influence it takes.
 * A JSON import names them by a string; airline-4x.test.ts holds the names to
 * Depth's.
 */
export const CITY_DEPTHS = Airline4XRules.city_depths as ReadonlyArray<{ name: Exclude<Depth, 'none'>; influence: number }>;

/** Each depth's name: its key, written whole. */
export const CITY_DEPTH_WORDS: Record<Depth, string> = {
  none: 'fourX.depth.NoPresence',
  presence: 'fourX.depth.Presence',
  base: 'fourX.depth.Base',
  franchise: 'fourX.depth.Franchise',
  stronghold: 'fourX.depth.Stronghold',
};

/** What each of the rules' depths opens: its key, written whole. */
export const DEPTH_OPENS_WORDS: Record<Exclude<Depth, 'none'>, string> = {
  presence: 'fourX.depthOpens.presence',
  base: 'fourX.depthOpens.base',
  franchise: 'fourX.depthOpens.franchise',
  stronghold: 'fourX.depthOpens.stronghold',
};

export const CITY_DEPTH_THRESHOLDS = CITY_DEPTHS.map(depth => depth.influence);
export const CITY_BASE_INFLUENCE = CITY_DEPTHS.find(depth => depth.name === 'base')!.influence;
export const CITY_FRANCHISE_INFLUENCE = CITY_DEPTHS.find(depth => depth.name === 'franchise')!.influence;
export const CITY_STRONGHOLD_INFLUENCE = CITY_DEPTHS.find(depth => depth.name === 'stronghold')!.influence;
/**
 * The steps a city's bar is drawn in: one for each Influence up to a
 * Stronghold, the deepest a city goes. A literal, because a bar draws only so
 * many steps; airports.test.ts holds it to the rules.
 */
export const CITY_BAR_STEPS = 10;
export function cityDepth(influence: number): Depth {
  const depth = [...CITY_DEPTHS].reverse().find(depth => influence >= depth.influence);
  return depth ? depth.name : 'none';
}
export function cityInfluence(airport: Airport | undefined, teamId: number): number {
  return airport?.influence_by_team?.[teamId] ?? 0;
}

/** Each team's Influence invested in cities: its permanent Influence, summed over every airport. */
export function investedInfluenceByTeam(airports: Record<number, Airport>): Record<number, number> {
  const totals: Record<number, number> = {};
  for (const airport of Object.values(airports)) {
    for (const [teamId, influence] of Object.entries(airport.influence_by_team ?? {})) {
      totals[Number(teamId)] = (totals[Number(teamId)] ?? 0) + influence;
    }
  }
  return totals;
}
/** Exact outcome of the next authoritative city action, including the free first HQ. */
export function getCityInvestmentPreview(airport: Airport | undefined, teamId: number, hqEstablished: boolean) {
  const additionalInfluence = hqEstablished ? 1 : Airline4XRules.hq_influence;
  const influence = cityInfluence(airport, teamId) + additionalInfluence;
  const collective = (airport?.collective_influence ?? 0) + additionalInfluence;
  const developmentLevel = AIRPORT_THRESHOLDS.filter(threshold => collective >= threshold).length;
  const tier = Airline4XRules.airport_levels[developmentLevel - 1];
  return {
    establishingHQ: !hqEstablished,
    cost: hqEstablished ? 1 : 0,
    additionalInfluence, influence, collective, developmentLevel,
    privateCapacity: influence * Airline4XRules.slots_per_influence,
    efficientCapacity: tier?.capacity ?? 0,
    category: tier?.category ?? 1,
  };
}

/**
 * The airport's congestion as the last update settled it: what the shared
 * airport's meter and the lines under it say (settledLoad), what your airline
 * pays (overflowPaid: the city's pip and its signpost), and the surcharge a
 * flight's page says for an aircraft that overflows here. The aircraft every
 * airline based here, the efficient positions they were settled on and the
 * band their load fell in; the overflow surcharge every airline here pays, and
 * each airline's efficient and overflow aircraft. The team's money is private,
 * delivered separately in its commercial snapshot.
 */
interface AirportCongestion {
  occupied: number;
  efficient_capacity: number;
  state: CongestionBand;
  surcharge_rate: number;
  teams: Record<number, { efficient: number; overflow: number }>;
}

/**
 * Highest aircraft category operable at the given runway level.
 * Only categories 1, 3, and 5 exist in the aircraft data.
 * L1-L2: cat 1, L3-L4: cat 3, L5: cat 5.
 */
export function maxCategoryForRunwayLevel(level: number): number {
  return Airline4XRules.airport_levels[Math.max(0, Math.min(level - 1, Airline4XRules.airport_levels.length - 1))].category;
}

/**
 * Whether an airport takes an aircraft: its runway level allows the aircraft's
 * category, the check the server makes at each end of a route
 * (MaxCategoryForRunwayLevel, segments.go). A route takes an aircraft when its
 * lower airport does, since a higher level never allows less.
 */
export function airportTakes(runwayLevel: number, category: number): boolean {
  return category <= maxCategoryForRunwayLevel(runwayLevel);
}

/**
 * The smallest airport development level that takes an aircraft of a
 * category: 1, an Airfield, for category 1; 3, a Major airport, for category
 * 3; 5, a Global airport, for category 5. The aircraft card names it only when
 * it is above an Airfield. Undefined for a category no airport takes.
 */
export function minimumAirportLevel(category: number): number | undefined {
  const index = Airline4XRules.airport_levels.findIndex((level) => level.category >= category);
  return index === -1 ? undefined : index + 1;
}

/**
 * The translation key that names an airport development level: "Major". A
 * level past the table is named as an Airfield, as the route builder names it.
 */
export function airportLevelKey(level: number): string {
  return AIRPORT_LEVEL_WORDS[level] ?? AIRPORT_LEVEL_WORDS[1];
}

/**
 * AirportPlaneAllocation tracks plane usage for a segment at an airport.
 */
interface AirportPlaneAllocation {
  /** Segment using this allocation */
  segment_id: number;
  /** Team that owns this segment */
  team_id: number;
  /** Number of planes using this allocation */
  plane_count: number;
}

/**
 * Airport represents an airport in the game.
 * Each city can have an airport that teams can develop and use for operations.
 */
export interface Airport {
  /** Same as the corresponding city ID from cities.json */
  id: number;
  /** Airport code from the corresponding city (e.g., "HNL", "YVR") */
  code: string;
  /** Shared development level (0-6), retained under the legacy field for aircraft capability checks. */
  runway_level: number;
  influence_by_team?: Record<number, number>;
  collective_influence?: number;
  development_level?: number;
  efficient_capacity?: number;
  congestion?: AirportCongestion;
  /** Per-team plane allocations within the airport's base capacity */
  base_allocations: AirportPlaneAllocation[];
  /** Cost of jet fuel at this airport */
  cost_jetfuel: number;
  /** Landing fee in dollars per metric ton */
  landing_fee: number;
}

// ==================== CAPACITY UTILITIES ====================

/**
 * A team's flight slots at an airport: those its Influence there holds
 * (`held`, so many per Influence), those its aircraft take (`used`), and
 * those still free (`available`). Every screen that says the city's slots
 * reads them here.
 */
export function calculateTeamPlaneCapacity(
  airport: Airport | undefined,
  teamId: number
): { used: number; available: number; held: number } {
  if (!airport || !teamId) {
    return { used: 0, available: 0, held: 0 };
  }

  const used = (airport.base_allocations ?? [])
    .filter(alloc => alloc.team_id === teamId)
    .reduce((sum, alloc) => sum + alloc.plane_count, 0);
  const held = Airline4XRules.slots_per_influence * cityInfluence(airport, teamId);
  return { used, available: Math.max(0, held - used), held };
}

// ==================== DEFAULT STATE BUILDER ====================

/**
 * Build the default (unmodified) airport state for all cities from static city data.
 * This mirrors the initial airport state created server-side in CreateMatchAirports.
 * Used as the base when processing airports_state messages — the server now only sends
 * airports that have been modified, so we merge them over this local default map.
 */
function buildDefaultAirports(): Record<number, Airport> {
  const defaults: Record<number, Airport> = {};
  for (const city of cities.getAll()) {
    defaults[city.id] = {
      id: city.id,
      code: city.code,
      runway_level: 0,
      influence_by_team: {},
      collective_influence: 0,
      development_level: 0,
      efficient_capacity: 0,
      base_allocations: [],
      cost_jetfuel: city.initial_cost_jetfuel,
      landing_fee: city.landing_fee,
    };
  }
  return defaults;
}

// ==================== STORE ====================

interface AirportsState {
  airports: Record<number, Airport>; // Map of city_id (number) -> Airport
  setAirports: (airports: Record<number, Airport>) => void;
  updateAirport: (id: number, airport: Airport) => void;
  reset: () => void;
}

const initialState = {
  airports: {},
};

/**
 * Airports Store
 * Manages airport state for the current match
 */
export const useAirportsStore = create<AirportsState>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set airports data (replaces existing)
       */
      setAirports: (airports) => {
        console.log('[AirportsStore] Setting airports data:', Object.keys(airports).length, 'airports');
        set({ airports }, false, 'setAirports');
      },

      /**
       * Update a specific airport by ID
       */
      updateAirport: (id, airport) => {
        console.log('[AirportsStore] Updating airport:', id);
        set(
          (state) => ({
            airports: {
              ...state.airports,
              [id]: airport,
            },
          }),
          false,
          'updateAirport'
        );
      },

      /**
       * Reset the airports store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'airports-store',
    }
  )
);

// ==================== HANDLERS ====================

/**
 * Handle OpCodeAirportsState message.
 * The server sends only modified airports (those that differ from the initial default state).
 * We build the full airport map by starting from local defaults derived from cities.csv,
 * then overwriting with whatever the server sent.
 */
export function handleAirportsState(data: { airports: Record<number, Airport> }) {
  // Start with default state for all 292 airports (derived from cities.csv locally)
  const merged = buildDefaultAirports();

  // Overlay only the airports the server actually sent (the modified ones)
  let serverCount = 0;
  for (const airport of Object.values(data.airports)) {
    if (airport && typeof airport.id === 'number') {
      merged[airport.id] = airport;
      serverCount++;
    }
  }

  console.log(
    `[AirportsHandler] Airports state: ${Object.keys(merged).length} total (${serverCount} from server, rest from local defaults)`
  );
  useAirportsStore.getState().setAirports(merged);
}

/**
 * An airport as another domain's message carries it, changed: a flight
 * added or removed took or gave back its capacity (segments.ts), a city's
 * unlock made it the team's headquarters (city-access.ts).
 */
export function handleAirportUpdate(airport: Airport): void {
  useAirportsStore.getState().updateAirport(airport.id, airport);
}

/** How loaded an airport is: the rules' congestion bands (congestion in airline-4x-rules.json), lightest first. */
type CongestionBand = 'normal' | 'busy' | 'overloaded' | 'severe' | 'gridlock';

/**
 * The rules' bands, each with what overflow pays in it; the server settles
 * which band a load falls in, and the client reads it (settledLoad). A JSON
 * import names them by a string; airline-4x.test.ts holds the names to
 * CongestionBand's.
 */
const CONGESTION = Airline4XRules.congestion as ReadonlyArray<{ name: CongestionBand; surcharge: number }>;

/** Each band's word: its key, written whole. */
export const CONGESTION_WORDS: Record<CongestionBand, string> = {
  normal: 'fourX.congestion.normal',
  busy: 'fourX.congestion.busy',
  overloaded: 'fourX.congestion.overloaded',
  severe: 'fourX.congestion.severe',
  gridlock: 'fourX.congestion.gridlock',
};

/** What overflow costs from the severe band up: half again, and more. */
const SEVERE_SURCHARGE = CONGESTION.find(band => band.name === 'severe')!.surcharge;

/**
 * Whether an airport's load is costly: the flights over its positions pay half
 * again and more, from the severe band up. The shared airport's meter turns
 * coral there.
 */
export function congestionCostly(surchargeRate: number): boolean {
  return surchargeRate >= SEVERE_SURCHARGE;
}

/**
 * An airport's load as the last update settled it, which is what its airlines
 * pay for: the aircraft every airline based here against the efficient
 * positions it had, the band that load fell in and the surcharge on the
 * overflow (the server's congestion record). A change since — a route added,
 * an investment that grows the airport — shows at the next update, as what it
 * costs does. An airport with no record flew nothing at that update.
 */
export function settledLoad(airport: Airport): { occupied: number; efficientCapacity: number; band: CongestionBand; surchargeRate: number } {
  const settled = airport.congestion;
  if (!settled) return { occupied: 0, efficientCapacity: airport.efficient_capacity ?? 0, band: 'normal', surchargeRate: 0 };
  return { occupied: settled.occupied, efficientCapacity: settled.efficient_capacity, band: settled.state, surchargeRate: settled.surcharge_rate };
}

/** What an airline pays for overflow at an airport: how many of its aircraft there overflow, of how many, and the surcharge on them. */
export interface OverflowPaid {
  overflow: number;
  aircraft: number;
  surchargeRate: number;
  /** Dollars per day from the private snapshot; absent for older saves. */
  dailyCost?: number;
}

/**
 * What your airline pays for overflow at an airport, as the last update
 * settled it (the server's congestion record): how many of your aircraft
 * based there overflow its efficient positions, of how many, and the
 * surcharge the overflow adds. Null where none of yours does — a rival's
 * crowding, a load changed since the update, an airport you fly nothing to:
 * congestion is said where it is paid, not where it might be.
 */
export function overflowPaid(airport: Airport | undefined, teamId: number | null | undefined, dailyCost?: number): OverflowPaid | null {
  const yours = teamId == null ? undefined : airport?.congestion?.teams?.[teamId];
  if (!yours || yours.overflow <= 0) return null;
  return { overflow: yours.overflow, aircraft: yours.efficient + yours.overflow, surchargeRate: airport?.congestion?.surcharge_rate ?? 0, ...(dailyCost === undefined ? {} : { dailyCost }) };
}
